import React from "react";
import { Link } from "react-router-dom";
import { MaskedReveal, FadeUp, ClipImage } from "@/components/motion/Reveal";
import { Marquee } from "@/components/motion/Marquee";
import { FIRM } from "@/data/site";
import { ArrowUpRight } from "lucide-react";
import People from "@/components/sections/People";

const values = [
  { n: "01", t: "Integrity", d: "Legal advice grounded in the Bar Council's ethical standards and personal conscience." },
  { n: "02", t: "Diligence", d: "Every brief is prepared as if it were to be argued the next morning." },
  { n: "03", t: "Discretion", d: "Client confidentiality is protected at every stage of the engagement." },
  { n: "04", t: "Results", d: "Practical, outcome-oriented representation over academic posturing." },
];

const why = [
  "A partner-led approach — Adv. Prasad Helkar personally supervises every mandate.",
  "Cross-practice depth: family, criminal, corporate, civil, testamentary, DRT and MACT under one roof.",
  "Rapid response for urgent bail and interim relief.",
  "Written engagement letters with clear fee estimates before we begin.",
  "Case reviews at documented milestones — no client is left in the dark.",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="pt-40 pb-16">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-8">
            <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 01 · About</div>
            <MaskedReveal as="h1" className="serif text-6xl md:text-[8vw] leading-[0.94]" lines={[<>A Mumbai law firm,</>, <em className="italic text-gold">quietly effective.</em>]} />
          </div>
          <div className="col-span-12 md:col-span-4">
            <FadeUp delay={0.4} className="text-[hsl(var(--muted-foreground))] leading-relaxed mt-6 md:mt-16">
              Founded in Kurla West, Helkar & Associates advises individuals, families, founders and financial institutions — combining boutique-firm attentiveness with the breadth of a full-service practice.
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Portrait + profile */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="col-span-12 md:col-span-5">
            <ClipImage
              src="https://images.unsplash.com/photo-1505664194779-8beaceb93744?q=80&w=1400&auto=format&fit=crop"
              alt="Law chambers, Kurla West Mumbai"
              className="aspect-[3/4] hairline border overflow-hidden"
              imgClassName="w-full h-full object-cover"
            />
            <div className="mt-4 flex items-center justify-between mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))]">
              <span>Chambers · Kurla West</span><span>Mumbai</span>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-8">
            <FadeUp>
              <div className="mono text-xs uppercase tracking-[0.32em] text-[hsl(var(--muted-foreground))] mb-4">Firm profile</div>
              <p className="serif text-2xl md:text-3xl leading-snug">
                We are a full-service law firm delivering strategic legal advice, litigation, dispute resolution, and corporate services to individuals, businesses, startups, financial institutions and corporations across India.
              </p>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="text-[hsl(var(--muted-foreground))] leading-relaxed">
                Our philosophy is simple: ethical, practical, cost-effective and result-oriented legal solutions — delivered with the professionalism and discretion our clients rely on. Whether it is a midnight bail application, a shareholder dispute, or a delicate testamentary matter, our chambers respond with the same rigour.
              </p>
            </FadeUp>

            <div className="grid grid-cols-2 gap-6">
              <FadeUp delay={0.2}>
                <div className="mono text-xs uppercase tracking-[0.28em] text-gold mb-2">Mission</div>
                <p className="text-[15px]">Provide unbiased, principled counsel and strong representation that honours the trust our clients place in us.</p>
              </FadeUp>
              <FadeUp delay={0.3}>
                <div className="mono text-xs uppercase tracking-[0.28em] text-gold mb-2">Vision</div>
                <p className="text-[15px]">A firm of counsel first — where clients are heard, precedent is respected, and outcomes are earned.</p>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-[hsl(var(--ivory-deep))]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 02 · Values</div>
          <MaskedReveal as="h2" className="serif text-5xl md:text-6xl leading-[0.98]" lines={["Four principles", <>we <em className="italic text-gold">refuse</em> to negotiate.</>]} />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-16">
            {values.map((v, i) => (
              <FadeUp key={v.n} delay={i * 0.1} className="border-t hairline pt-6">
                <div className="chapter-num text-3xl">{v.n}</div>
                <h3 className="serif text-2xl mt-3 mb-2">{v.t}</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{v.d}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* The People */}
      <People chapter="Chapter 03 · The People" />

      {/* Why */}
      <section className="py-20 md:py-28 bg-[hsl(var(--ivory-deep))]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-6">Chapter 04 · Why us</div>
            <MaskedReveal as="h2" className="serif text-5xl md:text-6xl leading-[0.98]" lines={[<>Why clients choose</>, <em className="italic text-gold">Helkar & Associates.</em>]} />
          </div>
          <div className="col-span-12 md:col-span-7">
            <ol className="divide-y hairline border-y hairline">
              {why.map((w, i) => (
                <FadeUp key={i} delay={i * 0.06} className="grid grid-cols-12 py-6 items-start gap-6">
                  <div className="col-span-2 chapter-num text-2xl">{String(i + 1).padStart(2, "0")}</div>
                  <div className="col-span-10 text-[16px] leading-relaxed">{w}</div>
                </FadeUp>
              ))}
            </ol>
            <FadeUp delay={0.5} className="mt-10">
              <Link to="/contact" className="btn-ink" data-testid="about-book">Book a Consultation <ArrowUpRight className="h-4 w-4" /></Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee items={["Committed to Justice", "Dedicated to Excellence", FIRM.altTagline, "Kurla West · Mumbai"]} />
    </div>
  );
}
